import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { message } from 'antd';
import { ConfirmModal } from 'components/Shared/ConfirmModal';
import { StepProps, StepViewer } from 'components/Shared/StepViewer';
import { useForm, useWatch } from 'components/UI/Form';
import { useOpenAccountAuth } from 'hooks/account/useOpenAccountAuth';
import { useHistory } from 'hooks/generic/useHistory';
import { useFireEvent } from 'hooks/useFireEvent';
import { useIsLoggedIn } from 'hooks/user/useIsLoggedIn';
import { useSearchParams } from 'hooks/useSearchParams';
import { RegisterCollectorStepTwo } from './RegisterCollector';
import {
  RegisterCompanyStepThree,
  RegisterCompanyStepTwo
} from './RegisterCompany';
import { RegisterCompanyPhaseTwo } from './RegisterCompanyPhaseTwo';
import {
  RegisterCreatorStepThree,
  RegisterCreatorStepTwo
} from './RegisterCreator';
import { RegisterFirstStep } from './RegisterFirstStep';
import { RegisterProvider } from '../../providers/RegisterProvider';
import { CheckEmailModal } from '../components/CheckEmailModal';
import { InfoBlockLoggedIn } from '../components/InfoBlockLoggedIn';
import { useAppDispatch } from 'appRedux/hooks';
import {
  getCompanyToken,
  registerUser as registerUserRequest
} from 'appRedux/restApi';
import { isMobile } from 'utils';
import { getCurrentLanguage } from 'utils/app/get-lang';
import { addQuery } from 'utils/generic/query-route';
import { EAccountRouteKeys } from 'constants/account/account-route-keys';
import { StepViewerEvents } from 'constants/app/eventbus-events';
import Success from 'assets/images/success-rocket.png';
import { AccountType, RestAxiosError } from 'interfaces';
import { IRegisterProps } from 'interfaces/Auth';

export const Register = () => {
  const registerId = useId();
  const searchParams = useSearchParams();
  const { isLoggedIn, isLoginPending } = useIsLoggedIn();
  const { openAccountLogin } = useOpenAccountAuth();
  const [searchValue, setSearchValue] = useState(searchParams['token']);
  const [selectedUserType, setSelectedUserType] = useState(0);
  const [checkEmailModalVisible, setCheckEmailModalVisible] = useState(false);
  const [companySuccessModalVisible, setCompanySuccessModalVisible] =
    useState(false);
  const { t } = useTranslation();
  const { history } = useHistory();
  const dispatch = useAppDispatch();
  const { fireEvent } = useFireEvent();
  const [form] = useForm();
  const email = useWatch('email', { form, preserve: true });
  const registerUserParams = (
    inputParams: Partial<IRegisterProps>
  ): ReturnType<typeof registerUserRequest> => {
    let _inputParams = { first_name: '', last_name: '', ...inputParams }; // first_name and last_name are required for backend
    switch (selectedUserType) {
      case 1:
        _inputParams.account_type = AccountType.User;
        break;
      case 2:
        _inputParams.account_type = AccountType.Creator;
        break;
      default:
        // For create company
        _inputParams = {
          ..._inputParams,
          account_type: AccountType.Company,
          token: searchValue
        };
        break;
    }
    _inputParams.locale = getCurrentLanguage();
    return registerUserRequest(_inputParams);
  };

  const { refetch, error, isFetching, data } = useQuery<
    Awaited<ReturnType<typeof registerUserParams>>,
    RestAxiosError
  >({
    queryKey: [EAccountRouteKeys.ACCOUNT_REGISTER, selectedUserType],
    queryFn: () => registerUserParams(form.getFieldsValue(true)),
    enabled: false,
    retry: false
  });

  const onSubmitRegister = () => {
    form
      .validateFields()
      .then(() => void refetch())
      .catch(({ errorFields }) => {
        const firstErrorField = errorFields[0].name[0];
        form.scrollToField(firstErrorField, {
          behavior: 'smooth',
          scrollMode: 'if-needed',
          block: 'center'
        });
      });
  };

  useEffect(() => {
    if (data) {
      if (selectedUserType === 3) {
        return;
      }
      if (selectedUserType === 0) {
        setCompanySuccessModalVisible(true);
      } else {
        setCheckEmailModalVisible(true);
      }
    }
  }, [data, selectedUserType]);

  useEffect(() => {
    if (error) {
      message.error(error?.response?.data.message);
    }
  }, [error]);

  useEffect(() => {
    fireEvent({
      key: isFetching
        ? StepViewerEvents.SetNextLoading
        : StepViewerEvents.UnsetNextLoading,
      id: registerId
    });
  }, [registerId, isFetching]);

  // TODO: refactor this
  useEffect(() => {
    if (searchParams['token'] && searchParams['token'].length > 0) {
      // If token is present in url then
      history.push(addQuery({ token: undefined }));
      dispatch(getCompanyToken({ token: searchParams['token'] }));
      setSearchValue(searchParams['token']);
    }
    return () => {
      setSearchValue('');
    };
  }, []);

  const companySteps = useMemo(() => {
    const _steps: StepProps[] = [
      {
        title: {
          text: t('account.createCompanyAccount'),
          shortText: ''
        },
        subtitle: t('account.fillCompanyInformation'),
        component: RegisterCompanyPhaseTwo,
        info: {
          show: !isMobile()
        },
        nextBtnText: t('account.submit'),
        loaderSize: 'large' as StepProps['loaderSize']
      }
    ];
    return _steps;
  }, []);

  const steps = useMemo(() => {
    const _steps: StepProps[] = [
      {
        title: {
          text: t('account.chooseYourUserType'),
          shortText: ''
        },
        subtitle: t('account.registerDescription'),
        component: RegisterFirstStep,
        info: {
          show: !isMobile(),
          text: selectedUserType ? undefined : ''
        },
        loaderSize: 'large' as StepProps['loaderSize']
      }
    ];
    switch (selectedUserType) {
      case 1:
        _steps.push({
          title: {
            text: t('account.registerAsCollector'),
            shortText: ''
          },
          subtitle: t('account.fillUserInformationAndMoveForward'),
          component: RegisterCollectorStepTwo,
          info: {
            show: !isMobile()
          },
          loaderSize: 'large' as StepProps['loaderSize'],
          handleToNextStepItself: true
        });
        break;
      case 2:
        _steps.push({
          title: {
            text: t('account.registerAsCreator'),
            shortText: ''
          },
          subtitle: t('account.fillUserGeneralInformation'),
          component: RegisterCreatorStepTwo,
          info: {
            show: !isMobile()
          },
          loaderSize: 'large' as StepProps['loaderSize'],
          handleToNextStepItself: true
        });
        _steps.push({
          title: {
            text: t('account.registerAsCreator'),
            shortText: ''
          },
          subtitle: t('account.fillUserPayoutInformation'),
          component: RegisterCreatorStepThree,
          info: {
            show: !isMobile()
          },
          loaderSize: 'large' as StepProps['loaderSize']
        });
        break;
      case 3:
        _steps.push({
          title: {
            text: t('account.registerAsCompany'),
            shortText: ''
          },
          subtitle: t('account.fillCompanyInformationAndMoveForward'),
          component: RegisterCompanyStepTwo,
          info: {
            show: !isMobile()
          },
          loaderSize: 'large' as StepProps['loaderSize'],
          handleToNextStepItself: true
        });
        _steps.push({
          title: {
            text: '',
            shortText: ''
          },
          subtitle: '',
          component: RegisterCompanyStepThree,
          info: {
            show: !isMobile()
          },
          loaderSize: 'large' as StepProps['loaderSize']
        });
        break;
      default:
        break;
    }
    return _steps;
  }, [selectedUserType]);

  const openLoginModal = () => {
    setCompanySuccessModalVisible(false);
    openAccountLogin();
  };

  if (isLoggedIn || isLoginPending) {
    return <InfoBlockLoggedIn loading={isLoginPending} />;
  }

  return (
    <RegisterProvider
      value={{
        form,
        selectedUserType,
        setSelectedUserType,
        eventId: registerId
      }}
    >
      <StepViewer
        width="100%"
        height={isMobile() ? '' : '624px'}
        eventId={registerId}
        header={{
          showStepsProgress: isMobile() ? 'text' : false
        }}
        footer={{
          navigationButtonsFullWidth: isMobile()
        }}
        onFinish={onSubmitRegister}
        steps={searchValue ? companySteps : steps}
      />
      {/* TODO: outside click checker works wrong in Modal component */}
      <CheckEmailModal
        checkEmailModalVisible={checkEmailModalVisible}
        email={email}
      />
      <ConfirmModal
        title={t('account.congratulations')}
        coloredTitle
        description={t('account.legalEntitySuccessModalDescription')}
        image={Success}
        visible={companySuccessModalVisible}
        onCancel={() => setCompanySuccessModalVisible(false)}
        onSubmit={openLoginModal}
        confirmText={t('account.login')}
      />
    </RegisterProvider>
  );
};
